"use client";

import { Grid2, Typography } from "@repo/ui";
import Section from "../components/Section";
import FastFillingSchedule from "@/components/courses/FastFillingSchedule";
import { useMemo } from "react";
import StructuredData from "@/components/common/StructuredData";
import { getWorkloadInISO } from "@/lib/helpers";
import { usePathname } from "next/navigation";
// import CourseDesignation from "@/components/courses/CourseDesignation";

type AboutCourseProps = {
  data: {
    heading?: string;
    description?: string;
    designation?: string;
    event?: ServiceEvent;
    category?: string;
    serviceTrainingDays?: string;
    reviews?: {
      name?: string;
      rating?: number;
      comment?: string;
      publishAt?: string;
    }[];
  };
  sectionInfo: PageSections;
};

const AboutCourse = ({ data, sectionInfo }: AboutCourseProps) => {
  const pathname = usePathname();
  const courseInfoSchema = useMemo(() => {
    const ratingCount = data?.reviews?.length || 0;
    const total =
      data?.reviews?.reduce(
        (sum, r) => sum + parseInt(r.rating?.toString() || "0"),
        0
      ) || 0;
    const ratingValue = ratingCount ? total / ratingCount : 0;
    return {
      "@context": "https://schema.org",
      "@id": process.env.NEXT_PUBLIC_APP_URL + pathname,
      "@type": "Course",
      name: data?.heading?.trim() ?? "",
      description:
        data?.description?.trim() ||
        data?.heading?.trim() ||
        "description not yet created",
      category: data?.category?.trim() ?? "",
      publisher: {
        "@type": "Organization",
        name: "Spoclearn",
        url: process.env.NEXT_PUBLIC_APP_URL,
      },
      provider: {
        "@type": "Organization",
        name: "Spoclearn",
        sameAs: process.env.NEXT_PUBLIC_APP_URL,
      },
      image: process.env.NEXT_PUBLIC_APP_URL + "/images/logo.svg",
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode:
          data?.event?.modeOfTraining === "Live Online Classroom"
            ? "Online"
            : "Blended",
        location: "Spoclearn",
        courseSchedule: {
          "@type": "Schedule",
          duration: getWorkloadInISO(data.serviceTrainingDays || "16 Hrs"),
          repeatFrequency: "Daily",
          repeatCount: 5,
          startDate: data?.event?.startDate || "2025-01-01",
          endDate: data?.event?.endDate || "2025-12-31",
        },
      },
      review:
        data?.reviews?.map((review) => ({
          "@type": "Review",
          author: {
            "@type": "Person",
            name: review.name || "Anonymous",
          },
          reviewBody: review.comment || "No comment provided",
          reviewRating: {
            "@type": "Rating",
            ratingValue: review.rating || "5",
            bestRating: review.rating || "5",
          },
          datePublished: review.publishAt || "2025-07-15",
        })) || [],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: ratingValue || 4.5,
        bestRating: total || 5,
        ratingCount: ratingCount || 25,
      },
      offers: {
        "@type": "Offer",
        url: process.env.NEXT_PUBLIC_APP_URL + pathname,
        priceCurrency: data?.event?.eventDetail?.currency,
        price: data?.event?.eventDetail?.price,
        availability: "https://schema.org/InStock",
        validFrom: data.event?.startDate,
        category: data?.category,
      },
    };
  }, [data, pathname]);

  return (
    <Section
      id={sectionInfo.sectionId}
      className={sectionInfo.sectionClass}
      data-title={sectionInfo.name}
    >
      <Grid2 container spacing={5}>
        <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8 }}>
          <Typography variant="h2" className="!mb-5">
            {data.heading}
          </Typography>
          <Typography className="[&_p]:mb-4 last:[&_p]:mb-0">
            {data.description}
          </Typography>

          {/* <CourseDesignation /> */}
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
          {/* Fast filling schedule card here  */}
          <FastFillingSchedule data={data.event} />
          <StructuredData data={courseInfoSchema} />
        </Grid2>
      </Grid2>
    </Section>
  );
};

export default AboutCourse;
